import React, { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { ugcDisplayRulesPath } from '@/common/app/path'
import { fetchRest } from '@/common/api/fetcher'

/** 6軸の配点は合計100点ちょうど（採点は100点満点で、振り分けの閾値もその前提） */
const WEIGHT_TOTAL = 100

/** 失敗時に fetchRest が throw した Response から、サーバの理由（detail）を取り出す */
const errorDetail = async (e: unknown, fallback: string) => {
  if (e instanceof Response) {
    const body = await e.json().catch(() => ({}))
    if (typeof body.detail === 'string') return body.detail
    return `${fallback}（HTTP ${e.status}）`
  }
  return fallback
}

// 共通スタイル（SmartHub デザイントークン）
const CARD = 'rounded-xl border border-secondary bg-surface shadow-area'
const TEXTAREA = 'w-full resize-y rounded-lg border border-border-input bg-surface px-2.5 py-1.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'

const TH = 'text-secondary border-b-2 border-secondary px-2.5 py-2 text-left text-[11px] font-semibold whitespace-nowrap'
const TD = 'text-primary px-2.5 py-2 align-middle'

/** AIスコアリング設定（backend/routers/ugc_scoring.py） */
interface ScoreWeights {
  time_continuity: number
  lifestyle: number
  recommendation: number
  concrete_effect: number
  text_quality: number
  image_quality: number
}

type ScoreAxis = keyof ScoreWeights

interface ScoreSettings {
  scoring_prompt_template?: string | null
  scoring_weights?: ScoreWeights | null
  auto_scoring_enabled?: boolean
  /** 毎日の自動採点の時刻（日本時間の「時」0〜23） */
  auto_scoring_hour?: number
  auto_publish_enabled?: boolean
  auto_publish_score?: number
  needs_review_score?: number
  /** 未設定のときに使われる既定のプロンプト（サーバが返す） */
  default_prompt?: string | null
}

/** スコアリング履歴の1行 */
interface ScoreHistoryRow {
  id: number
  ugc_item_id?: number
  score: number
  /** サーバはJSON文字列で返す */
  categories?: string | null
  reasoning?: string | null
  model_version?: string | null
  scored_at?: string | null
  created_at?: string | null
}

/** 一括スコアリングの結果 */
interface BatchResult {
  status?: 'started' | 'no_items' | 'error'
  count?: number
  message?: string
  [key: string]: unknown
}

/** 試し打ちの結果 */
interface TestResult extends Partial<ScoreWeights> {
  score: number
  reasoning?: string | null
  categories?: string[] | string | null
}

export default function UGCAIScoreSettings() {
  const { siteId } = useParams()
  const [settings, setSettings] = useState<ScoreSettings | null>(null)
  const [history, setHistory] = useState<ScoreHistoryRow[]>([])
  const [historyTotal, setHistoryTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [scoring, setScoring] = useState(false)
  const [batchResult, setBatchResult] = useState<BatchResult | null>(null)
  const [testCaption, setTestCaption] = useState('')
  const [testResult, setTestResult] = useState<TestResult | null>(null)
  const [testing, setTesting] = useState(false)
  const [editPrompt, setEditPrompt] = useState(false)
  const [promptText, setPromptText] = useState('')
  const [saving, setSaving] = useState(false)
  const [weights, setWeights] = useState({
    time_continuity: 15, lifestyle: 15, recommendation: 15,
    concrete_effect: 20, text_quality: 20, image_quality: 15,
  })

  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([
      fetchRest<ScoreSettings>(`/api/sites/${siteId}/ugc/score/settings`),
      fetchRest<{ items?: ScoreHistoryRow[]; total?: number }>(
        `/api/sites/${siteId}/ugc/score/history?per_page=10`
      ),
    ])
      .then(([s, hist]) => {
        setSettings(s)
        setPromptText(s.scoring_prompt_template ?? '')
        if (s.scoring_weights) setWeights(s.scoring_weights)
        setHistory(hist.items || [])
        setHistoryTotal(hist.total || 0)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [siteId])

  const handleBatchScore = () => {
    setScoring(true)
    setBatchResult(null)
    fetchRest<BatchResult>(`/api/sites/${siteId}/ugc/score/batch`, {
      method: 'POST',
      body: JSON.stringify({ max_items: 50 }),
    })
      .then(d => setBatchResult(d))
      // 2026-09-29 修正：理由を出さずに「エラー」とだけ出していたので、原因が分からなかった
      .catch(async (e) => setBatchResult({ status: 'error', message: await errorDetail(e, 'バッチを開始できませんでした') }))
      .finally(() => setScoring(false))
  }

  const handleTest = () => {
    if (!testCaption.trim()) return
    setTesting(true)
    setTestResult(null)
    fetchRest<TestResult>(`/api/sites/${siteId}/ugc/score/test`, {
      method: 'POST',
      body: JSON.stringify({ caption: testCaption, source_type: 'review' }),
    })
      .then(setTestResult)
      .catch(() => setTestResult({ score: 0, reasoning: 'エラーが発生しました' }))
      .finally(() => setTesting(false))
  }

  const handleSaveSettings = () => {
    setSaving(true)
    setSaveError(null)
    if (!settings) return
    fetchRest(`/api/sites/${siteId}/ugc/score/settings`, {
      method: 'PUT',
      body: JSON.stringify({
        auto_scoring_enabled: settings.auto_scoring_enabled,
        auto_scoring_hour: settings.auto_scoring_hour ?? 5,
        // 空文字は「デフォルトに戻す」。null はサーバで「変更なし」扱いになる
        scoring_prompt_template: promptText,
        scoring_weights: weights,
        auto_publish_enabled: settings.auto_publish_enabled,
        auto_publish_score: settings.auto_publish_score,
        needs_review_score: settings.needs_review_score,
      }),
    })
      .then(() => setEditPrompt(false))
      // 閾値の逆転などはサーバが理由を返す。握りつぶすと
      // 「保存したのに反映されない」に見える（fetchRest は失敗時に Response を throw する）
      .catch(async (e) => setSaveError(await errorDetail(e, '保存できませんでした')))
      .finally(() => setSaving(false))
  }

  const weightSum = Object.values(weights).reduce((a, b) => a + b, 0)
  const weightsOk = weightSum === WEIGHT_TOTAL

  const scoreBadge = (score: number) =>
    score >= 70
      ? 'bg-surface-success text-success'
      : score >= 40
        ? 'bg-surface-warning text-warning'
        : 'bg-surface-critical text-critical'

  const scoreFill = (score: number) =>
    score >= 70
      ? 'var(--color-fill-success)'
      : score >= 40
        ? 'var(--color-fill-warning)'
        : 'var(--color-fill-critical)'

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='🤖 AIスコアリング設定'
        description='レビューや投稿の質をAIが100点満点で採点します。スコアはサイトへの表示順や自動振り分けに使われます'
      />

      {/* 2026-09-29 追加：採点すると何が良いのか・次に何をするのかが分からないという指摘への対応 */}
      <div className={`${CARD} mb-4 grid grid-cols-1 gap-4 p-5 md:grid-cols-2`}>
        <div>
          <h3 className='text-primary m-0 text-sm font-semibold'>採点すると何が良いか</h3>
          <ul className='text-secondary mt-2 mb-0 list-disc space-y-1 pl-5 text-xs leading-relaxed'>
            <li>サイトの表示枠が<b>質の高い順（おすすめ順）</b>に並び、低いスコアの投稿を出さないようにもできます</li>
            <li>「スコアで自動的に振り分ける」をONにすると、確認の手間が減ります（高スコアは自動公開、低スコアは却下）</li>
            <li>コンテンツ鮮度アラートで、入れ替え候補（同じ商品でスコアの高い投稿）が出るようになります</li>
          </ul>
        </div>
        <div>
          <h3 className='text-primary m-0 text-sm font-semibold'>採点したら次にやること</h3>
          <ol className='text-secondary mt-2 mb-0 list-decimal space-y-1 pl-5 text-xs leading-relaxed'>
            <li>
              まだ採点していない投稿があれば「今すぐバッチ実行」で採点する
              （以後は毎日{String(settings?.auto_scoring_hour ?? 5).padStart(2, '0')}:00に自動で採点）
            </li>
            <li>
              <Link to={ugcDisplayRulesPath(Number(siteId))} className='text-link'>サイト表示枠設定</Link>
              で、並び順「おすすめ順」や表示するスコアの下限を決める
            </li>
            <li>任せてよいと思えたら「スコアで自動的に振り分ける」をONにする</li>
          </ol>
        </div>
      </div>

      {loading ? (
        <div className='text-secondary py-16 text-center text-sm'>読み込み中...</div>
      ) : (
        <>
          {/* 設定カード */}
          <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
            <div className={`${CARD} p-5`}>
              <h3 className='text-primary m-0 text-sm font-semibold'>自動スコアリング</h3>
              <div className='mt-3 mb-4 flex items-center gap-3'>
                <div
                  onClick={() => setSettings({ ...settings, auto_scoring_enabled: !settings?.auto_scoring_enabled })}
                  className={`relative h-[26px] w-12 cursor-pointer rounded-[13px] transition-colors ${settings?.auto_scoring_enabled ? 'bg-fill-brand' : 'bg-fill-tertiary'}`}
                >
                  <div
                    className='bg-surface shadow-area absolute top-[3px] h-5 w-5 rounded-full transition-all'
                    style={{ left: settings?.auto_scoring_enabled ? 25 : 3 }}
                  />
                </div>
                <span className={`text-sm font-semibold ${settings?.auto_scoring_enabled ? 'text-emphasis' : 'text-secondary'}`}>
                  {settings?.auto_scoring_enabled ? 'ON' : 'OFF'}
                </span>
              </div>
              {/* 2026-09-29 変更：実行時刻をサイトごとに選べるようにした（以前は 05:00 固定） */}
              <div className='mb-2 flex flex-wrap items-center gap-2'>
                <span className='text-secondary text-xs'>毎日</span>
                <select
                  value={settings?.auto_scoring_hour ?? 5}
                  onChange={(e) => setSettings({ ...settings, auto_scoring_hour: Number(e.target.value) })}
                  disabled={!settings?.auto_scoring_enabled}
                  className='border-border-input bg-input rounded-md border px-2 py-1 text-xs'
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>{`${String(h).padStart(2, '0')}:00`}</option>
                  ))}
                </select>
                <span className='text-secondary text-xs'>（日本時間）に採点する</span>
              </div>
              <p className='text-secondary text-xs leading-relaxed'>
                ONにすると、まだ採点していない投稿をAIが自動で採点します（1回につき最大20件）。<br />
                時刻を変えたら下の「設定を保存」を押してください。
              </p>

              {/* スコアによる振り分け（70/50分岐） */}
              <div className='bg-surface-secondary mt-4 rounded-lg p-3.5'>
                <div className='flex items-center gap-3'>
                  <div
                    onClick={() => setSettings({ ...settings, auto_publish_enabled: !settings?.auto_publish_enabled })}
                    className={`relative h-[26px] w-12 cursor-pointer rounded-[13px] transition-colors ${settings?.auto_publish_enabled ? 'bg-fill-brand' : 'bg-fill-tertiary'}`}
                  >
                    <div
                      className='bg-surface shadow-area absolute top-[3px] h-5 w-5 rounded-full transition-all'
                      style={{ left: settings?.auto_publish_enabled ? 25 : 3 }}
                    />
                  </div>
                  <span className='text-primary text-xs font-semibold'>スコアで自動的に振り分ける</span>
                </div>
                <div className='mt-3 flex flex-wrap items-center gap-2'>
                  <span className='text-secondary text-xs'>スコア</span>
                  <input
                    type='number' min={0} max={100}
                    value={settings?.auto_publish_score ?? 70}
                    onChange={(e) => setSettings({ ...settings, auto_publish_score: Number(e.target.value) })}
                    className='border-border-input bg-input w-16 rounded-md border px-2 py-1 text-xs'
                  />
                  <span className='text-secondary text-xs'>以上は公開、</span>
                  <input
                    type='number' min={0} max={100}
                    value={settings?.needs_review_score ?? 50}
                    onChange={(e) => setSettings({ ...settings, needs_review_score: Number(e.target.value) })}
                    className='border-border-input bg-input w-16 rounded-md border px-2 py-1 text-xs'
                  />
                  <span className='text-secondary text-xs'>以上は要確認、それ未満は却下</span>
                </div>
                <p className='text-secondary mt-2.5 text-[11px] leading-relaxed'>
                  顧客のサイトに出るものをAIのスコアだけで決める設定です。OFFの間はスコアを付けるだけで、状態は変わりません。<br />
                  許諾が取れていないUGCは、スコアが高くても公開されず要確認に入ります。<br />
                  すでに人が公開・非表示・却下を決めたものは動かしません。
                </p>
                <div className='mt-3 flex flex-wrap items-center gap-3'>
                  <Button color={ButtonColor.PRIMARY} disabled={saving || !weightsOk} onClick={handleSaveSettings}>
                    {saving ? '保存中...' : '設定を保存'}
                  </Button>
                  {!weightsOk && <span className='text-critical text-xs'>下のウェイトを合計{WEIGHT_TOTAL}点にしてください</span>}
                  {saveError && <span className='text-critical text-xs'>{saveError}</span>}
                </div>
              </div>

              {/* 6軸ウェイト設定 */}
              <div className='bg-surface-secondary mt-4 rounded-lg p-3.5'>
                <div className='mb-2.5 flex items-center justify-between'>
                  <span className='text-primary text-xs font-semibold'>
                    📊 採点6軸のウェイト（合計:{' '}
                    <span className={weightsOk ? 'text-success' : 'text-critical'}>{weightSum}</span> / {WEIGHT_TOTAL}点）
                  </span>
                  <button onClick={() => setWeights({ time_continuity: 15, lifestyle: 15, recommendation: 15, concrete_effect: 20, text_quality: 20, image_quality: 15 })} className='text-link cursor-pointer text-[10px]'>リセット</button>
                </div>
                {([
                  { key: 'time_continuity', label: '⏱️ 時間軸（継続利用）', desc: '使い続けた期間・変化', example: '「3ヶ月使って肌のキメが整った」' },
                  { key: 'lifestyle', label: '🏠 ライフスタイル訴求', desc: '日常への組み込み', example: '「朝のルーティンに欠かせない」' },
                  { key: 'recommendation', label: '👥 推薦意向', desc: '他者への紹介意志', example: '「友達にも勧めた」' },
                  { key: 'concrete_effect', label: '✨ 具体的効果・実感', desc: '抽象的でなく変化', example: '「毛穴が目立たなくなった」' },
                  { key: 'text_quality', label: '📝 テキスト品質', desc: '文量・文脈の十分さ', example: '100文字以上の具体的な記述' },
                  { key: 'image_quality', label: '📸 画像品質', desc: '商品の視認性・明るさ', example: '商品が明確に写った明るい写真' },
                ] as { key: ScoreAxis; label: string; desc: string; example: string }[]).map(axis => (
                  <div key={axis.key} className='mb-2.5'>
                    <div className='mb-0.5 flex items-center justify-between'>
                      <div>
                        <span className='text-primary text-xs font-semibold'>{axis.label}</span>
                        <span className='text-tertiary ml-2 text-[10px]'>{axis.desc}</span>
                      </div>
                      <span className='text-emphasis w-10 text-right text-sm font-bold'>{weights[axis.key]}</span>
                    </div>
                    <div className='flex items-center gap-2'>
                      <input
                        type='range' min={0} max={30} value={weights[axis.key]}
                        onChange={e => setWeights({ ...weights, [axis.key]: Number(e.target.value) })}
                        className='h-1 flex-1 accent-[var(--color-fill-brand)]'
                      />
                    </div>
                    <div className='text-tertiary mt-px text-[9px]'>例: {axis.example}</div>
                  </div>
                ))}
                {/* 2026-09-29 追加：合計100点以外を保存できていた（採点は100点満点のため） */}
                <p className={`mt-1 mb-2 text-[11px] ${weightsOk ? 'text-secondary' : 'text-critical'}`}>
                  {weightsOk
                    ? '配点の合計が100点です。重視したい軸の配点を上げ、ほかを下げて調整してください。'
                    : `合計が${weightSum}点です。あと${Math.abs(WEIGHT_TOTAL - weightSum)}点${weightSum > WEIGHT_TOTAL ? '減らして' : '増やして'}、合計${WEIGHT_TOTAL}点にしてください。`}
                </p>
                <Button color={ButtonColor.PRIMARY} disabled={saving || !weightsOk} onClick={handleSaveSettings}>
                  {saving ? '保存中...' : 'ウェイトを保存'}
                </Button>
              </div>

              <div className='mt-4'>
                <Button color={ButtonColor.PRIMARY} disabled={scoring} onClick={handleBatchScore}>
                  {scoring ? 'スコアリング中...' : '🚀 今すぐバッチ実行'}
                </Button>
              </div>
              {batchResult && (
                <div className={`mt-3 rounded-lg p-2.5 text-xs ${batchResult.status === 'started' ? 'bg-surface-success text-success' : 'bg-surface-warning text-warning'}`}>
                  {batchResult.status === 'started' ? `✅ ${batchResult.count}件のスコアリングを開始しました` :
                   batchResult.status === 'no_items' ? '⚠️ まだ採点していないUGCはありません（すべて採点済みです）' :
                   `❌ ${batchResult.message || 'エラーが発生しました'}`}
                </div>
              )}
            </div>

            {/* テスト実行 */}
            <div className={`${CARD} p-5`}>
              <h3 className='text-primary m-0 text-sm font-semibold'>スコアリングテスト</h3>
              <p className='text-secondary mb-3 text-xs'>テキストを入力してAIスコアリングをテストします（DBに保存されません）</p>
              <textarea
                value={testCaption}
                onChange={e => setTestCaption(e.target.value)}
                placeholder='レビューやキャプションを入力...'
                className={`${TEXTAREA} h-20`}
              />
              <div className='mt-2'>
                <Button color={ButtonColor.SECONDARY} disabled={testing || !testCaption.trim()} onClick={handleTest}>
                  {testing ? '判定中...' : 'テスト実行'}
                </Button>
              </div>

              {testResult && (
                <div className='bg-surface-secondary mt-3 rounded-lg border border-secondary p-3.5'>
                  <div className='mb-2 flex items-center gap-3'>
                    <div
                      className='text-on-bg-fill flex h-12 w-12 items-center justify-center rounded-full text-base font-bold'
                      style={{ background: scoreFill(testResult.score) }}
                    >{testResult.score}</div>
                    <div>
                      <div className='text-secondary text-xs'>スコア</div>
                      <div className='text-tertiary text-[11px]'>
                        {/* サーバはJSON文字列で返すことがある。配列とどちらでも読めるようにする */}
                        {Array.isArray(testResult.categories)
                          ? testResult.categories.join(', ')
                          : testResult.categories || ''}
                      </div>
                    </div>
                  </div>
                  <p className='text-secondary m-0 text-xs leading-normal'>{testResult.reasoning}</p>
                  <div className='mt-2.5 grid grid-cols-3 gap-1.5'>
                    {[
                      { label: '時間軸', val: testResult.time_continuity, max: weights.time_continuity },
                      { label: 'ライフスタイル', val: testResult.lifestyle, max: weights.lifestyle },
                      { label: '推薦意向', val: testResult.recommendation, max: weights.recommendation },
                      { label: '具体的効果', val: testResult.concrete_effect, max: weights.concrete_effect },
                      { label: 'テキスト', val: testResult.text_quality, max: weights.text_quality },
                      { label: '画像品質', val: testResult.image_quality, max: weights.image_quality },
                    ].map(d => (
                      <div key={d.label} className='bg-surface rounded-md border border-secondary py-1.5 text-center'>
                        <div className='text-primary text-base font-bold'>{d.val ?? 0}/{d.max}</div>
                        <div className='text-tertiary text-[9px]'>{d.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* プロンプト設定 */}
          <div className={`${CARD} mt-4 p-5`}>
            <div className='mb-3 flex items-center justify-between'>
              <h3 className='text-primary m-0 text-sm font-semibold'>スコアリングプロンプト</h3>
              <Button color={ButtonColor.SECONDARY} onClick={() => setEditPrompt(!editPrompt)}>
                {editPrompt ? '閉じる' : '編集'}
              </Button>
            </div>
            {/* 2026-09-29 追加：いつ効くのかが分からないという指摘への対応 */}
            <div className='bg-surface-secondary text-secondary mb-3 rounded-lg p-3 text-[11px] leading-relaxed'>
              AIに渡す採点の指示文です。<b>保存した後に行う採点すべて</b>に使われます
              （毎日の自動採点・「今すぐバッチ実行」・右上のスコアリングテスト）。
              すでに付いたスコアは変わりません（付け直すには、対象を採点し直してください）。<br />
              編集しない場合は、上の「採点6軸のウェイト」の配点を入れた標準の指示文を使います。
              <b>ここを編集すると、ウェイトより優先</b>されます（配点も指示文の中で指定してください）。
            </div>
            {editPrompt ? (
              <>
                <textarea
                  value={promptText}
                  onChange={e => setPromptText(e.target.value)}
                  className={`${TEXTAREA} h-[300px] font-mono text-xs leading-relaxed`}
                />
                <div className='mt-2.5 flex gap-2'>
                  <Button color={ButtonColor.PRIMARY} disabled={saving} onClick={handleSaveSettings}>
                    {saving ? '保存中...' : '保存'}
                  </Button>
                  <Button color={ButtonColor.SECONDARY} onClick={() => { setPromptText('') }}>
                    デフォルトに戻す（保存で確定）
                  </Button>
                </div>
              </>
            ) : (
              <div className='bg-surface-secondary text-secondary max-h-[200px] overflow-auto rounded-lg p-3.5 font-mono text-xs leading-relaxed whitespace-pre-wrap'>
                {promptText || settings?.default_prompt || '（デフォルトプロンプト使用中）'}
              </div>
            )}
          </div>

          {/* スコアリング履歴 */}
          <div className={`${CARD} mt-4 p-5`}>
            <h3 className='text-primary m-0 mb-3 text-sm font-semibold'>スコアリング履歴 ({historyTotal}件)</h3>
            {history.length > 0 ? (
              <div className='overflow-x-auto'>
                <table className='w-full border-collapse text-xs'>
                  <thead>
                    <tr className='bg-surface-secondary'>
                      <th className={TH}>UGC ID</th>
                      <th className={TH}>スコア</th>
                      <th className={TH}>カテゴリ</th>
                      <th className={TH}>判定理由</th>
                      <th className={TH}>モデル</th>
                      <th className={TH}>日時</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(hist => (
                      <tr key={hist.id} className='border-b border-secondary'>
                        <td className={TD}>#{hist.ugc_item_id}</td>
                        <td className={TD}>
                          <span className={`inline-block rounded-[10px] px-2.5 py-0.5 text-xs font-bold ${scoreBadge(hist.score)}`}>{hist.score}</span>
                        </td>
                        <td className={`${TD} text-secondary text-[10px]`}>{hist.categories}</td>
                        <td className={`${TD} text-secondary max-w-[250px] overflow-hidden text-ellipsis whitespace-nowrap`}>{hist.reasoning}</td>
                        <td className={`${TD} text-tertiary text-[10px]`}>{hist.model_version}</td>
                        <td className={`${TD} text-tertiary text-[10px]`}>{hist.scored_at?.slice(0, 16)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className='text-tertiary py-5 text-center text-[13px]'>スコアリング履歴がありません</p>
            )}
          </div>
        </>
      )}
    </Main>
  )
}
